import { PhotographIcon } from '@heroicons/react/outline';
import { config } from '@koolumbus/web/config';
import { getPropertyImageURL } from '@koolumbus/web/utils';
import { PropertyImage } from '@prisma/client';
import React, { useMemo, useState } from 'react';
import Lightbox from 'react-image-lightbox';
import { PropertyData } from '../../../lib/models';

interface PropertyImagesGridProps {
  property: PropertyData;
}

const PropertyImagesGrid: React.FC<React.PropsWithChildren<PropertyImagesGridProps>> = ({
  property,
}) => {
  const [isLightboxOpen, setLightboxOpen] = useState(false);

  const sortedImages = useMemo(() => {
    const result = Array.from(property.images);
    result.sort((a, b) => (a.index < b.index ? -1 : 1));
    return result;
  }, [property]);

  const images = [0, 1, 2].map((index) => {
    return sortedImages.length > index
      ? getPropertyImageURL(config.env, property.tenantId, sortedImages[index].id)
      : `/assets/images/post-image-${index === 1 ? '2' : '1'}.jpg`;
  });

  return (
    <>
      <div className="relative cursor-pointer" onClick={() => setLightboxOpen(true)}>
        <div className="max-w-full md:rounded-md aspect-w-16 aspect-h-9 md:aspect-w-16 md:aspect-h-6">
          <div className="flex w-full justify-between md:rounded-md">
            <div
              className="w-full md:w-[64%] h-full md:rounded-md md:mr-2 shadow bg-no-repeat bg-cover bg-center relative overflow-hidden"
              style={{ backgroundImage: `url("${images[0]}")` }}
            />

            <div className="hidden md:flex flex-col justify-between md:w-[36%] h-full md:rounded-md">
              <div
                className="w-full h-1/2 md:mb-2 shadow md:rounded-md bg-no-repeat bg-cover bg-center"
                style={{ backgroundImage: `url("${images[1]}")` }}
              />

              <div
                className="w-full h-1/2 shadow md:rounded-md bg-no-repeat bg-cover bg-center"
                style={{ backgroundImage: `url("${images[2]}")` }}
              />
            </div>
          </div>
        </div>

        <button
          type="button"
          className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 shadow-lg inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          <PhotographIcon className="-ml-1 mr-2.5 h-5 w-5" aria-hidden="true" />
          Gallery
        </button>
      </div>

      <PropertyImagesLightbox
        images={sortedImages}
        isOpen={isLightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
};

interface PropertyImagesLightboxProps {
  images: PropertyImage[];
  isOpen: boolean;
  onClose: () => void;
}

const PropertyImagesLightbox: React.FC<React.PropsWithChildren<PropertyImagesLightboxProps>> = ({
  images,
  isOpen,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!isOpen) {
    return null;
  }

  const getImageSource = (index: number) => {
    const image = images[index];
    return getPropertyImageURL(config.env, image.tenantId, image.id);
  };

  return (
    <Lightbox
      wrapperClassName="property-images-lightbox"
      reactModalStyle={{ overlay: { zIndex: 1300 }, content: { zIndex: 1300 } }}
      mainSrc={getImageSource(currentIndex)}
      nextSrc={currentIndex < images.length - 1 ? getImageSource(currentIndex + 1) : undefined}
      prevSrc={currentIndex > 0 ? getImageSource(currentIndex - 1) : undefined}
      onCloseRequest={onClose}
      onMovePrevRequest={() => setCurrentIndex(currentIndex - 1)}
      onMoveNextRequest={() => setCurrentIndex(currentIndex + 1)}
      enableZoom={false}
      toolbarButtons={[
        <p className="mr-4 text-sm leading-[50px]" key={0}>{`${currentIndex + 1} / ${
          images.length
        }`}</p>,
      ]}
    />
  );
};

export default PropertyImagesGrid;
